import * as React from 'react';
import './FeaturedCarousel.css';
import './Share.css';
import axios from 'axios';
import queryString from 'query-string';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Slide from '@mui/material/Slide';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import useAnalyticsEventTracker from './useAnalyticsEventTracker';
import { sendData, truncateAtWord, copyToClipboard } from '../functions/Utils.js';

const TITLE_MAX_CHARS = 36;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// sortType=1 (Hotness/"Meilleur") orders by IsHighlight DESC first in the
// backend (DealService.FindActif), so all currently-active highlights sit at
// the top of the response. We fetch a generous pool and keep ALL highlighted
// items — no display cap, because editorial picks are curated and finite.
const CAROUSEL_SORT = 1;
const CAROUSEL_FETCH_POOL = 50;
const PROMO_HASH = '#openFeaturedPromo';

// Amazon image URLs come back as thumbnails (e.g. `_SL160_`). The carousel
// cards are 168–184px wide and the image fills the full square with
// `object-fit: cover`, so the thumbnail upscales blurry. Swap the size
// token to `_SL500_` for a sharper render at no extra request cost.
const upscaleAmazonImage = (url) =>
  (typeof url === 'string' ? url.replace(/_SL\d+_/, '_SL500_') : url);

export default function FeaturedCarousel({ selected, search, isHidden, refreshKey, excludeId }) {
  const { t } = useTranslation();
  const [items, setItems] = React.useState([]);
  const [openPromo, setOpenPromo] = React.useState(window.location.hash === PROMO_HASH);
  const [deal, setDeal] = React.useState({});
  const [copied, setCopied] = React.useState(false);

  const trackRef = React.useRef(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const gaEventTracker = useAnalyticsEventTracker('Featured');
  const isFacebookInAppBrowser = navigator.userAgent.match(/FBAN|FBAV|FB_IAB/i);

  // Serialize the selected category ids into a stable string so the effect
  // re-fires only when the actual selection changes (not on every re-render's
  // fresh array reference).
  const selectedKey = React.useMemo(
    () => (Array.isArray(selected) ? selected.join('|') : ''),
    [selected]
  );

  React.useEffect(() => {
    if (!selectedKey) {
      setItems([]);
      return;
    }
    const POSTS_ENDPOINT = process.env.REACT_APP_API_URL + '/Deals';
    const url = queryString.stringifyUrl({
      url: POSTS_ENDPOINT,
      query: {
        page: 1,
        limit: CAROUSEL_FETCH_POOL,
        search: search || '',
        categories: selectedKey,
        sortType: CAROUSEL_SORT,
      },
    });
    axios
      .get(url, { validateStatus: (s) => s < 500 })
      .then((res) => {
        if (res.status < 400) {
          const data = Array.isArray(res.data) ? res.data : [];
          // Backend already groups highlights at the top via the Hotness sort,
          // so this filter just drops the tail of non-highlighted hot deals.
          setItems(data.filter((d) => d.isHighlight));
        } else {
          setItems([]);
        }
      })
      .catch(() => setItems([]));
  }, [selectedKey, search, refreshKey]);

  React.useEffect(() => {
    const onHashChange = () => setOpenPromo(window.location.hash === PROMO_HASH);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const updateScrollState = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < maxScroll - 2);
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = trackRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [items, updateScrollState]);

  const scrollByCards = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('.featuredCard');
    const cardWidth = card ? card.getBoundingClientRect().width : 160;
    const step = direction * (cardWidth * 2 + 24);
    el.scrollBy({ left: step, behavior: 'smooth' });
  };

  const handleCardClick = (d) => {
    if (isFacebookInAppBrowser) {
      sessionStorage.setItem('dealId', d.id);
    }
    gaEventTracker('ClickFeatured', '', d.id);
    if (!d.promoCode) {
      window.open(d.dealUrl, '_blank');
      sendData(d.id, 'Click');
      return;
    }
    setDeal(d);
    setCopied(false);
    window.location.hash = PROMO_HASH;
  };

  const handleClose = () => {
    window.history.back();
    if (!isFacebookInAppBrowser) setCopied(false);
  };

  // Exclude the "top deal of the day" (always a highlight — it's picked from
  // the highlighted pool), so it isn't shown both as the DealOfTheDay banner
  // and again in this rail (App passes its id).
  const visibleItems = excludeId != null ? items.filter((d) => d.id !== excludeId) : items;

  if (isHidden || visibleItems.length === 0) return null;

  const railTitle = t('Featured_Carousel_Title', {
    defaultValue: i18n.language === 'fr' ? 'Deals du jour' : 'Deals of the day',
  });

  return (
    <React.Fragment>
      <section className="featuredCarousel" aria-label={railTitle}>
        <div className="featuredCarousel-header">
          <span className="featuredCarousel-title">{railTitle}</span>
        </div>
        <div className="featuredCarousel-viewport">
          <button
            type="button"
            className="featuredCarousel-arrow featuredCarousel-arrow--prev"
            onClick={() => scrollByCards(-1)}
            aria-label={t('Featured_Carousel_Prev', { defaultValue: i18n.language === 'fr' ? 'Précédent' : 'Previous' })}
            disabled={!canScrollLeft}
            tabIndex={canScrollLeft ? 0 : -1}
          >
            <ChevronLeftIcon />
          </button>
          <div className="featuredCarousel-track" role="list" ref={trackRef}>
            {visibleItems.map((d) => {
              const title = i18n.language === 'fr' ? d.titleFr : d.titleEn;
              const displayTitle = truncateAtWord(title, TITLE_MAX_CHARS);
              return (
                <button
                  key={(refreshKey || 0) + '-' + d.id}
                  type="button"
                  role="listitem"
                  className="featuredCard"
                  onClick={() => handleCardClick(d)}
                  aria-label={title}
                  title={title}
                >
                  <div className="featuredCard-imgWrap">
                    <img
                      className="featuredCard-img"
                      alt={title}
                      src={d.imageUrl ? upscaleAmazonImage(d.imageUrl) : '/EmptyImage.jpg'}
                      loading="lazy"
                    />
                    {d.totalDiscountPCT > 0 && (
                      <span className="featuredCard-badge">-{d.totalDiscountPCT}%</span>
                    )}
                  </div>
                  <div className="featuredCard-body">
                    <span className="featuredCard-title">{displayTitle}</span>
                    {d.priceFinal > 0 && (
                      <div className="featuredCard-pricing">
                        <span className="featuredCard-price">${d.priceFinal.toFixed(2)}</span>
                        {d.priceRegular > d.priceFinal && (
                          <span className="featuredCard-original">${d.priceRegular.toFixed(2)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="featuredCarousel-arrow featuredCarousel-arrow--next"
            onClick={() => scrollByCards(1)}
            aria-label={t('Featured_Carousel_Next', { defaultValue: i18n.language === 'fr' ? 'Suivant' : 'Next' })}
            disabled={!canScrollRight}
            tabIndex={canScrollRight ? 0 : -1}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </section>

      <Dialog
        className="DialogBox titleColor1"
        sx={{ m: 0, p: 0 }}
        open={openPromo}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ m: 0, p: 2, fontSize: '35px' }}>{t('Promocode_Popup_Title')}</DialogTitle>
        <IconButton
          aria-label="fermer"
          onClick={handleClose}
          size="large"
          sx={{ position: 'absolute', right: 0, top: 12, color: '#0d1b36' }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
        <DialogContent className="DialogBoxContent">
          <DialogContentText id="detailDialog">
            <div>
              <span>{t('Promocode_Popup_Content')}</span>
              <br />
              <br />
              <button
                className={copied ? 'codePromo copied' : 'codePromo'}
                onClick={() => {
                  copyToClipboard(deal.promoCode).then((ok) => { if (ok) setCopied(true); });
                }}
              >
                {deal.promoCode}
                <br />
                {copied && <span>{t('Promocode_Popup_Clicked_Code_Msg')}</span>}
              </button>
              <br />
              <span className="codePromoUnder">{t('Promocode_Popup_Click_Code_Msg')}</span>
            </div>
            <br />
            <Button
              className="button-color1"
              onClick={() => {
                handleClose();
                window.open(deal.dealUrl, '_blank');
                sendData(deal.id, 'Click');
                gaEventTracker('Click', '', deal.id);
              }}
            >
              {t('Promocode_Popup_Button')}
            </Button>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
